/**
 * SupportPage (Поддержка) — help hub for the storefront (ТЗ §8, §9).
 *
 * Three sections:
 *   - «Написать в поддержку» → topic picker (5 categories) → opens a ticket and lands the
 *     user in the ticket chat ({@link SupportTicketPage}).
 *   - «Мои обращения» — the user's tickets with their RU status.
 *   - «Частые вопросы» (FAQ) — an accordion of question/answer pairs (ТЗ §9).
 *
 * Order-specific questions are still fastest in the order chat, so that shortcut stays.
 */
import { useState } from "react";

import { Badge, Button, Card, Sheet } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  SUPPORT_CATEGORY_OPTIONS,
  SUPPORT_STATUS_PRESENTATION,
  createTicket,
  useFaq,
  useMyTickets,
  type SupportCategory,
  type SupportTicketListItem,
} from "@entities/support";
import { ApiError } from "@shared/api";
import { routes, useNavigate } from "@shared/router";

import styles from "./SupportPage.module.css";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { readonly detail?: unknown } | null;
    if (
      payload !== null &&
      typeof payload === "object" &&
      typeof payload.detail === "string"
    ) {
      return payload.detail;
    }
  }
  return "Не удалось создать обращение. Попробуйте ещё раз.";
}

export function SupportPage(): JSX.Element {
  const navigate = useNavigate();
  const { data: tickets, refetch: refetchTickets } = useMyTickets();
  const { data: faq } = useFaq();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [category, setCategory] = useState<SupportCategory | null>(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useBackButton(() => {
    navigate(routes.home());
  });

  const openSheet = (): void => {
    setCategory(null);
    setText("");
    setError(null);
    setSheetOpen(true);
  };

  const closeSheet = (): void => {
    if (submitting) {
      return;
    }
    setSheetOpen(false);
  };

  const submit = (): void => {
    if (category === null || text.trim().length === 0 || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    createTicket({ category, text: text.trim() })
      .then((ticket) => {
        setSheetOpen(false);
        refetchTickets();
        navigate(routes.supportTicket(ticket.id));
      })
      .catch((err: unknown) => {
        setError(errorText(err));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Поддержка</h1>

      <Card className={styles.section}>
        <h2 className={styles.sectionTitle}>Нужна помощь?</h2>
        <p className={styles.body}>
          Опишите проблему — выберите тему обращения, и мы ответим в чате внутри приложения.
        </p>
        <Button variant="primary" size="lg" fullWidth onClick={openSheet}>
          Написать в поддержку
        </Button>
      </Card>

      {tickets && tickets.length > 0 ? (
        <Card className={styles.section}>
          <h2 className={styles.sectionTitle}>Мои обращения</h2>
          <div className={styles.tickets}>
            {tickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                onOpen={() => navigate(routes.supportTicket(ticket.id))}
              />
            ))}
          </div>
        </Card>
      ) : null}

      <Card className={styles.section}>
        <h2 className={styles.sectionTitle}>Вопрос по заказу</h2>
        <p className={styles.body}>
          Быстрее всего — написать прямо в чат заказа: продавец отвечает там и обычно в
          течение 10 минут.
        </p>
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          onClick={() => navigate(routes.orders())}
        >
          Мои заказы
        </Button>
      </Card>

      {faq && faq.length > 0 ? (
        <Card className={styles.section}>
          <h2 className={styles.sectionTitle}>Частые вопросы</h2>
          <div className={styles.faqList}>
            {faq.map((item) => (
              <FaqRow
                key={item.id}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </div>
        </Card>
      ) : null}

      <Sheet open={sheetOpen} onClose={closeSheet} title="Новое обращение">
        <div className={styles.sheetBody}>
          <p className={styles.sheetHint}>Выберите тему обращения</p>
          <div className={styles.categories}>
            {SUPPORT_CATEGORY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={styles.category}
                data-active={category === option.value}
                onClick={() => setCategory(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Опишите вопрос</span>
            <textarea
              className={styles.textarea}
              value={text}
              maxLength={4000}
              rows={4}
              placeholder="Например: не приходит подтверждение оплаты по заказу…"
              onChange={(e) => setText(e.target.value)}
            />
          </label>

          {error !== null ? (
            <p className={styles.errorNote} role="alert">
              {error}
            </p>
          ) : null}

          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={
              category === null || text.trim().length === 0 || submitting
            }
            onClick={submit}
          >
            {submitting ? "Создаём…" : "Создать обращение"}
          </Button>
        </div>
      </Sheet>
    </div>
  );
}

interface TicketRowProps {
  readonly ticket: SupportTicketListItem;
  readonly onOpen: () => void;
}

function TicketRow({ ticket, onOpen }: TicketRowProps): JSX.Element {
  const status = SUPPORT_STATUS_PRESENTATION[ticket.status];
  return (
    <button type="button" className={styles.ticket} onClick={onOpen}>
      <div className={styles.ticketHead}>
        <span className={styles.ticketNumber}>{ticket.number}</span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      <span className={styles.ticketCategory}>{ticket.category_display}</span>
      {ticket.last_message ? (
        <span className={styles.ticketPreview}>{ticket.last_message}</span>
      ) : null}
      <span className={styles.ticketDate}>
        {formatDate(ticket.last_message_at)}
      </span>
    </button>
  );
}

interface FaqRowProps {
  readonly question: string;
  readonly answer: string;
}

function FaqRow({ question, answer }: FaqRowProps): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.faqItem}>
      <button
        type="button"
        className={styles.faqQuestion}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{question}</span>
        <span className={styles.faqChevron} data-open={open} aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      {open ? <p className={styles.faqAnswer}>{answer}</p> : null}
    </div>
  );
}
